/**
 * Voice Card Overlay — the web half of the app's VoiceSkillSheet.
 *
 * Renders skill-emitted F8 cards as a glass panel beside (desktop) or
 * below (mobile) the orb. In voice mode, `skill_invoke` actions just dismiss
 * the card (there's no chat composer to send to).
 *
 * PPL-534 — a card here is a decision surface, not a notification. Every group
 * used to start a 15 s timer the moment it mounted, i.e. while f8 was still
 * speaking the sentence that introduced it, so a `message_draft` could vanish
 * before it had been read. Nothing expires a card now: it closes on the × in
 * the header, on a card action, or when the next session starts. The durable
 * copy is the chat thread — the proxy writes each skill turn (summary + cards)
 * there as a bot message, and F8Chat reopens that thread when the call ends.
 */

import { X } from 'react-feather';
import F8Card from './cards/F8Card';
import { useCardActionHandler } from '../../hooks/useCardActionHandler';
import type { F8CardData } from './cards/cardHelpers';
import type { CardAction } from '../../utils/cardActionHandler';
import '../../styles/cards.css';

export interface VoiceCardGroup {
  /** Unique key — typically epoch ms when the group was emitted. */
  timestamp: number;
  /** Optional skill name shown in the panel eyebrow. */
  skillName?: string;
  /** Cards to render inside this group. */
  cards: F8CardData[];
}

function CardGroup({
  group,
  onDismiss,
}: {
  group: VoiceCardGroup;
  onDismiss: (timestamp: number) => void;
}) {
  // In voice mode, skill_invoke just dismisses (no composer to forward to).
  const handleAction = useCardActionHandler({
    onSkillInvoke: () => onDismiss(group.timestamp),
  });

  const onCardAction = (action: CardAction) => {
    if (action?.action === 'skill_invoke') {
      onDismiss(group.timestamp);
      return;
    }
    handleAction(action);
  };

  return (
    <div className="f8c-voice-card-group">
      {group.cards.map((card, idx) => (
        <F8Card key={idx} card={card} onAction={onCardAction} />
      ))}
    </div>
  );
}

export default function VoiceCardOverlay({
  activeCards,
  onDismiss,
}: {
  activeCards: VoiceCardGroup[];
  onDismiss: (timestamp: number) => void;
}) {
  if (!activeCards || activeCards.length === 0) return null;

  // Eyebrow = most recently emitted group's skillName, fallback to a generic label.
  const latest = activeCards[activeCards.length - 1];
  const eyebrow = latest?.skillName?.trim() || 'Voice skill';

  return (
    <div className="f8c-voice-overlay">
      <div className="f8c-voice-overlay-header">
        <span className="f8c-voice-overlay-eyebrow">{eyebrow}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="f8c-voice-overlay-live">
            <span className="f8c-voice-overlay-live-dot" />
            live
          </span>
          <button
            type="button"
            className="f8c-voice-dismiss-btn"
            onClick={() => onDismiss(latest.timestamp)}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="f8c-voice-overlay-scroll">
        {activeCards.map((group) => (
          <CardGroup key={group.timestamp} group={group} onDismiss={onDismiss} />
        ))}
      </div>
      <div className="f8c-voice-overlay-footer">
        <span className="f8c-voice-overlay-footer-icon">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <rect x="9" y="3" width="6" height="12" rx="3" stroke="#f0abfc" strokeWidth="2" />
            <path
              d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8"
              stroke="#f0abfc"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <span className="f8c-voice-overlay-footer-text">
          Say a follow-up, or tap a card to act on it.
        </span>
      </div>
    </div>
  );
}
